import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Users, 
  Clock, 
  Check, 
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  User,
  DollarSign,
  Play,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RotationCalendar({ clubId, isCreator = false, onPayoutProcessed }) {
  const [rotation, setRotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (clubId) {
      loadRotation();
    }
  }, [clubId]);

  const loadRotation = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clubs/${clubId}/rotation`, {
        withCredentials: true
      });
      setRotation(response.data);
    } catch (error) {
      console.error('Load rotation error:', error);
      toast.error('Failed to load rotation schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayout = async () => {
    if (!isCreator) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/clubs/${clubId}/process-payout`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      loadRotation();
      onPayoutProcessed?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-500';
      case 'current': return 'bg-amber-500';
      case 'pending': return 'bg-stone-300';
      default: return 'bg-stone-200';
    }
  };

  const getStatusBadge = (status, isYou) => {
    if (status === 'paid') {
      return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
    }
    if (isYou) {
      return <Badge className="bg-indigo-100 text-indigo-700">You</Badge>;
    }
    return <Badge variant="outline">Pending</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Bi-weekly',
      monthly: 'Monthly',
      yearly: 'Yearly'
    };
    return labels[freq] || freq;
  };

  if (loading) {
    return (
      <Card className="border-stone-200">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!rotation || !rotation.rotation_schedule?.length) {
    return (
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
            Rotation Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 mb-4">No rotation schedule set up yet</p>
            {isCreator && (
              <p className="text-sm text-stone-400">
                Set up a rotation schedule from club settings
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentPosition = rotation.current_position || 0;
  const schedule = rotation.rotation_schedule || [];
  const currentRecipient = schedule[currentPosition];

  return (
    <Card className="border-stone-200 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center text-xl">
              <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
              Rotation Calendar
            </CardTitle>
            <CardDescription className="mt-1">
              {getFrequencyLabel(rotation.payout_frequency)} payouts • {rotation.total_members} members
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-indigo-600">
              ${rotation.pot_per_payout?.toFixed(2) || '0.00'}
            </div>
            <div className="text-sm text-stone-500">per payout</div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Your Position Banner */}
        {rotation.your_position && (
          <div className="bg-indigo-600 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <User className="w-5 h-5 mr-2" />
                <span className="font-medium">Your Position: #{rotation.your_position}</span>
              </div>
              <div className="text-right">
                <div className="text-sm opacity-80">Your Payout Date</div>
                <div className="font-semibold">{formatDate(rotation.your_payout_date)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Current Payout Section */}
        {currentRecipient && currentPosition < schedule.length && (
          <div className="px-6 py-4 bg-amber-50 border-b border-amber-100">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-amber-600 font-medium mb-1">Next Payout</div>
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center mr-3">
                    <User className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-stone-900">
                      {currentRecipient.display_name}
                    </div>
                    <div className="text-sm text-stone-500">
                      {formatDate(currentRecipient.payout_date)}
                    </div>
                  </div>
                </div>
              </div>
              
              {isCreator && (
                <Button
                  onClick={handleProcessPayout}
                  disabled={processing}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="process-payout-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Process Payout
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Rotation List */}
        <div className="divide-y divide-stone-100 max-h-96 overflow-y-auto">
          {schedule.map((entry, index) => {
            const isYou = entry.user_id === rotation.your_position ? true : index + 1 === rotation.your_position;
            const isCurrent = index === currentPosition;
            const isPaid = entry.status === 'paid';
            
            return (
              <motion.div
                key={entry.position}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`px-6 py-4 flex items-center ${
                  isCurrent ? 'bg-amber-50' : isPaid ? 'bg-green-50/50' : ''
                }`}
              >
                {/* Position Number */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                  isPaid ? 'bg-green-100 text-green-700' :
                  isCurrent ? 'bg-amber-100 text-amber-700' :
                  'bg-stone-100 text-stone-500'
                }`}>
                  {isPaid ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="font-bold">{entry.position}</span>
                  )}
                </div>

                {/* Member Info */}
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className={`font-medium ${isPaid ? 'text-green-700' : 'text-stone-900'}`}>
                      {entry.display_name}
                    </span>
                    <span className="ml-2">
                      {getStatusBadge(entry.status, isYou)}
                    </span>
                  </div>
                  <div className="text-sm text-stone-500">
                    {formatDate(entry.payout_date)}
                  </div>
                </div>

                {/* Amount */}
                <div className="text-right">
                  <div className={`font-semibold ${isPaid ? 'text-green-600' : 'text-stone-600'}`}>
                    ${rotation.pot_per_payout?.toFixed(2) || '0.00'}
                  </div>
                  {isPaid && (
                    <div className="text-xs text-green-600">Received</div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer Stats */}
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-200">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-stone-600">
              <Users className="w-4 h-4 mr-1" />
              {schedule.filter(s => s.status === 'paid').length} of {schedule.length} paid
            </div>
            <div className="flex items-center text-stone-600">
              <Clock className="w-4 h-4 mr-1" />
              {getFrequencyLabel(rotation.payout_frequency)} cycle
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentPosition / schedule.length) * 100}%` }}
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
